import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { ProjectConfigUpdate } from '@completion/actions';
import { Store } from '@ngrx/store';
import { ProjectConfig } from '@completion/models';
import { ProjectConfigCategory, ProjectConfigMeta, PROJECT_CONFIG_METADATA } from '../../../../shared/enums/project-config';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmChangeDialogComponent,
  ConfirmChangeDialogData,
} from '../../../../shared/confirm-change-dialog/confirm-change-dialog.component';
import { getResourceState } from '@completion/selectors';
import { ResourceStatus, ResourceType } from '@completion/enums';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-project-config-section',
  templateUrl: './project-config-section.component.html',
  styleUrls: ['./project-config-section.component.scss'],
  standalone: false,
})
export class ProjectConfigSectionComponent implements OnDestroy, OnChanges {
  @Input() category: ProjectConfigCategory;
  @Input() items: Array<{ meta: ProjectConfigMeta; config: ProjectConfig }> = [];

  // shared selection model for this section only
  selectedOptions: string[] = [];
  currentConfigMeta: ProjectConfigMeta | null = null;
  private readonly destroys$ = new Subject<void>();

  private readonly metaByKey = new Map<string, ProjectConfigMeta>(PROJECT_CONFIG_METADATA.map((m) => [m.key, m]));

  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {
    this.store
      .select(getResourceState, ResourceType.ProjectConfigUpdate)
      .pipe(takeUntil(this.destroys$))
      .subscribe((state) => {
        if (state && state.status === ResourceStatus.Success) {
          if (this.currentConfigMeta?.action) {
            const followUp = this.currentConfigMeta.action();
            this.store.dispatch(followUp);
          }
          // Reset so we don't re-dispatch on the next emission
          this.currentConfigMeta = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroys$.next();
    this.destroys$.complete();
  }

  ngOnChanges(): void {
    // whenever inputs change, rebuild local selection from current values
    this.selectedOptions = (this.items || []).filter((i) => i.config.value === 'true').map((i) => i.config.name);
  }

  onSelectionChange(currentSelection: string[]): void {
    this.selectedOptions = currentSelection || [];

    for (const item of this.items) {
      const cfg = item.config;
      const isSelectedNow = this.selectedOptions.includes(cfg.name);
      const wasTrue = cfg.value === 'true';
      if (isSelectedNow === wasTrue) {
        continue;
      }

      const meta = this.metaByKey.get(cfg.name) || item.meta;
      this.currentConfigMeta = meta;
      const applyChange = () => {
        cfg.value = isSelectedNow ? 'true' : 'false';
        this.store.dispatch(new ProjectConfigUpdate(cfg));
      };

      if (meta?.requireConfirmDialog) {
        const dialogData: ConfirmChangeDialogData = {
          title: meta.label,
          message: meta.confirmDialogMessage || 'Are you sure you want to change this setting?',
          severity: 'project',
        };
        const ref = this.dialog.open(ConfirmChangeDialogComponent, { data: dialogData });
        ref.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            applyChange();
          } else {
            if (wasTrue) {
              this.selectedOptions.push(cfg.name);
            } else {
              this.selectedOptions = this.selectedOptions.filter((n) => n !== cfg.name);
            }
          }
        });
      } else {
        applyChange();
      }

      break;
    }
  }
}
